import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { PhaseOption } from '@/shared/workflowAITypes';

interface PhaseOptionPickerProps {
  options: PhaseOption[];
  onConfirm: (selectedIds: string[]) => void;
  disabled?: boolean;
}

export function PhaseOptionPicker({ options, onConfirm, disabled }: PhaseOptionPickerProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    if (disabled) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    onConfirm(Array.from(selected));
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider px-1">
        Select workflow phases
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((opt, index) => {
          const isSelected = selected.has(opt.id);

          return (
            <motion.button
              key={opt.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
              onClick={() => toggle(opt.id)}
              disabled={disabled}
              className={cn(
                'group relative flex items-start gap-3 rounded-xl border p-3 text-left transition-all duration-200',
                'hover:shadow-md hover:-translate-y-0.5',
                isSelected
                  ? 'border-workfinity-300 bg-workfinity-50/80 shadow-sm ring-1 ring-workfinity-200'
                  : 'border-slate-200 bg-white/60 hover:border-slate-300',
                disabled && 'opacity-60 pointer-events-none',
              )}
            >
              {/* Checkbox indicator */}
              <div
                className={cn(
                  'flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 mt-0.5',
                  isSelected
                    ? 'border-workfinity-500 bg-workfinity-500'
                    : 'border-slate-300 bg-white group-hover:border-slate-400',
                )}
              >
                {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-slate-800">{opt.label}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{opt.description}</p>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-slate-400">
          {selected.size} phase{selected.size !== 1 ? 's' : ''} selected
        </span>
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={selected.size === 0 || disabled}
          className="bg-gradient-to-r from-workfinity-500 to-workfinity-600 hover:from-workfinity-600 hover:to-workfinity-700 text-white shadow-sm"
        >
          <Check className="h-3.5 w-3.5 mr-1.5" />
          Confirm Phases
        </Button>
      </div>
    </div>
  );
}
